import React from 'react';
import { AgnesBadge, AgnesButton, AgnesCheckbox } from '@agnes/ui';
import { RubricaAnaliseLote } from '@/types/rubricas/analise-lote/RubricaAnaliseLote';
import { BaseComponentProps } from '@/components/BaseComponentProps';
import { Check, ChevronDown, MessageSquare } from 'lucide-react';
import { GRAUS_RISCO } from '@/constants/ConstantGrauRiscoRubrica';
import { BadgeGrauExposicaoRisco } from '@/components/analise/BadgeGrauExposicaoRisco';
import { BadgeEmAnalise } from '@/components/analise/BadgeEmAnalise';
import { BadgeRubricaNatureza } from '@/components/rubricas/natureza/BadgeRubricaNatureza';
import { brl } from '@/utils/locale';

export interface CardRubricasAnaliseLoteProps extends BaseComponentProps {
  lote: RubricaAnaliseLote;
  /** Aba "Analisado": o selo conta as analisadas em vez das pendentes. */
  analisado?: boolean;
  onAprovarGrupo?: () => void;
  selecionado?: boolean;
  selecionando?: boolean;
  onSelecaoGrupoChange?: (selecionado: boolean) => void;
  onClickConversacao: () => void;
}

export function CardRubricasAnaliseLote({
  children,
  dataQa,
  lote,
  analisado = false,
  onAprovarGrupo,
  selecionado = false,
  selecionando = false,
  onSelecaoGrupoChange,
  onClickConversacao,
}: CardRubricasAnaliseLoteProps) {
  const [aberto, setAberto] = React.useState(false);

  return (
    <div className="rounded-lg border border-agnes-border bg-agnes-bg-surface p-4">
      <div className="flex flex-row items-start justify-between gap-3">
        <div className="flex flex-row items-center justify-between gap-4">
          <AgnesCheckbox
            aria-label={`Selecionar grupo ${lote.descricao}`}
            checked={selecionado}
            disabled={selecionando || lote.totais.rubricas === 0}
            onCheckedChange={(checked) => onSelecaoGrupoChange?.(checked === true)}
          />

          <div className={'flex flex-col items-start justify-start gap-1'}>
            <div className={'flex flex-row items-center justify-start gap-2'}>
              <span className={'font-semibold'}>{lote.descricao}</span>

              <AgnesBadge size={'sm'} variant={'success'}>
                {analisado
                  ? `${lote.totais.analisadas} ${lote.totais.analisadas === 1 ? 'analisado' : 'analisados'}`
                  : `${lote.totais.paraAnalisar} para analisar`}
              </AgnesBadge>
            </div>

            <div
              className={
                'flex flex-row items-start justify-start gap-x-2 gap-y-1 flex-wrap text-sm text-agnes-text-muted'
              }
            >
              <span>{lote.totais.rubricas} rubricas</span>
              <span>·</span>
              <span>
                {lote.totais.naturezas}{' '}
                {lote.totais.naturezas > 1 ? 'naturezas distintas' : 'natureza'}
              </span>
              <span>·</span>
              <span>{lote.totais.empregadores} CNPJs</span>
            </div>

            <div
              className={
                'flex flex-row items-start justify-start gap-2 text-sm text-agnes-text-muted'
              }
            >
              Tipo: <span className="text-agnes-text">{lote.tipoNomes.join(', ') || '—'}</span>
            </div>

            <p className="flex flex-row items-center justify-start gap-x-4 gap-y-1 flex-wrap text-sm">
              <span className="text-agnes-text-muted">Grau de risco:</span>

              {GRAUS_RISCO.map(({ value, totalKey }) => (
                <span key={value} className="flex items-center gap-1.5">
                  {value === 'EM_ANALISE' ? (
                    <BadgeEmAnalise size="sm" dataQa={`${dataQa}-${encodeURIComponent(value)}`} />
                  ) : (
                    <BadgeGrauExposicaoRisco
                      size="sm"
                      dataQa={`${dataQa}-${encodeURIComponent(value)}`}
                      grau={value}
                    />
                  )}

                  {brl(lote.totaisPorGrauRisco[totalKey])}
                </span>
              ))}
            </p>

            <p className="flex flex-row items-center justify-start gap-x-1 gap-y-1 flex-wrap text-sm">
              <span className="text-agnes-text-muted">Naturezas:</span>

              {lote.naturezaCodigos.map((naturezaCodigo) => (
                <BadgeRubricaNatureza key={naturezaCodigo} codigo={naturezaCodigo} />
              ))}
            </p>
          </div>
        </div>

        <div className="flex flex-col items-end gap-2">
          {!analisado && (
            <AgnesButton
              dataQa={dataQa ? `${dataQa}-aprovar-grupo` : undefined}
              variant="primary"
              size="sm"
              type="button"
              startIcon={<Check className="h-4 w-4" />}
              disabled={!onAprovarGrupo || lote.totais.paraAnalisar === 0}
              onClick={onAprovarGrupo}
            >
              Aprovar grupo
            </AgnesButton>
          )}

          <div hidden>
            <AgnesButton
              variant="text"
              size="sm"
              type="button"
              startIcon={<MessageSquare className="h-4 w-4" />}
              onClick={onClickConversacao}
            >
              Conversação
            </AgnesButton>
          </div>

          <AgnesButton
            variant="text"
            size="sm"
            type="button"
            endIcon={
              <ChevronDown
                className={`h-4 w-4 transition-transform ${aberto ? 'rotate-180' : ''}`}
              />
            }
            onClick={() => setAberto((s) => !s)}
          >
            Ver rubricas
          </AgnesButton>
        </div>
      </div>

      {aberto && <div className="mt-4 pt-4 border-t border-agnes-border">{children}</div>}
    </div>
  );
}
